import React from 'react';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Plus } from 'lucide-react';
import { usePlans } from './hooks/usePlans';
import { PlansTable, PlanDialog } from './components';
import { PlansPageProps } from './types';

export default function PlansPage({ isDark }: PlansPageProps) {
  const {
    plans,
    variants,
    isLoading,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    isSubmitting,
    setIncludeInactive,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
  } = usePlans();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uz-UZ', {
      style: 'currency',
      currency: 'UZS',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDuration = (value: number, unit: string) => {
    const unitMap: Record<string, string> = {
      week: 'нед',
      month: 'мес',
      year: 'год',
    };
    return `${value} ${unitMap[unit] || unit}`;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn(
            'text-2xl lg:text-3xl font-bold tracking-tight',
            isDark ? 'text-white' : 'text-[#111111]'
          )}>Тарифы</h1>
          <p className={cn(
            'text-sm mt-1',
            isDark ? 'text-slate-400' : 'text-slate-500'
          )}>Управление тарифами подписки</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch checked={includeInactive} onCheckedChange={setIncludeInactive} />
            <Label className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>Показать неактивные</Label>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-[#C9A227] hover:bg-[#C9A227]/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Создать тариф
          </Button>
        </div>
      </div>

      {/* Plans Table */}
      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden transition-all duration-300',
        isDark 
          ? 'bg-[#1A1A22] shadow-lg shadow-black/10' 
          : 'bg-white shadow-lg shadow-slate-200/50'
      )}>
        <PlansTable
          isDark={isDark}
          plans={plans}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          formatPrice={formatPrice}
          formatDuration={formatDuration}
        />
      </Card>

      {/* Create Dialog */}
      <PlanDialog
        isDark={isDark}
        variants={variants}
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isSubmitting={isSubmitting}
        title="Создать тариф"
        description="Добавить новый тариф"
        submitLabel="Создать"
      />

      {/* Edit Dialog */}
      <PlanDialog
        isDark={isDark}
        variants={variants}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleUpdate}
        isSubmitting={isSubmitting}
        title="Редактировать тариф"
        description="Обновить данные тарифа"
        submitLabel="Сохранить"
      />
    </div>
  );
}
